import {
  ButtonGroup,
  HStack,
  Tag,
  TagLabel,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import AccountCircleSharpIcon from '@gamma/ui/icons/AccountCircleSharpIcon';
import HelpSharpIcon from '@gamma/ui/icons/HelpSharpIcon';
import { AccordionPanel, FlushPanelContent } from '@gamma/ui/layout';
import { useContext, useMemo } from 'react';
import { CellProps, Column } from 'react-table';

import { TimestampCell } from '@investigator/shared/components';
import { DETECTION_AUTO_CLOSE_DURATION } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  DetectionAutoCloseConfig,
} from '../../../../shared/providers';
import { ResetDetectionAutoCloseConfig } from '../ResetDetectionAutoCloseConfig';
import { UpdateDetectionAutoCloseConfig } from '../UpdateDetectionAutoCloseConfig';

const { title, tableColumns, descriptionValue, tooltip, defaultDays, days } =
  i18n.pages.detectionSettings;

export const DetectionSettingsDashboard = () => {
  const defaultValues = { duration: '7', updated_by: 'N/A', updated_on: 'N/A' };
  const { userLicense, userPrivileges = {} } = useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const { detection_auto_close_config = defaultValues } =
    userPrivileges?.[tenant ?? ''] ?? {};

  const detectionAutoCloseConfigList = useMemo(
    () => [detection_auto_close_config],
    [detection_auto_close_config],
  );

  const dataTableColumns: Column<DetectionAutoCloseConfig>[] = [
    {
      Header: tableColumns.description,
      disableSortBy: true,
      Cell: () => (
        <HStack>
          <Text>{descriptionValue}</Text>
          <Tooltip label={tooltip} placement="top" hasArrow shouldWrapChildren>
            <HelpSharpIcon color="text.primary" size="sm" />
          </Tooltip>
        </HStack>
      ),
    },
    {
      Header: tableColumns.value,
      disableSortBy: true,
      accessor: 'duration',
      Cell: (props: CellProps<DetectionAutoCloseConfig>) =>
        props.value === DETECTION_AUTO_CLOSE_DURATION.toString() ? (
          <Text>{i18n.formatString(defaultDays, props.value)}</Text>
        ) : (
          <Text>{i18n.formatString(days, props.value)}</Text>
        ),
    },
    {
      Header: tableColumns.modifiedDate,
      disableSortBy: true,
      accessor: 'updated_on',
      Cell: (props: CellProps<DetectionAutoCloseConfig>) =>
        props.value !== 'N/A' ? (
          <TimestampCell
            timestamp={props.value}
            isFromNow={false}
            format="ll"
          />
        ) : (
          <Text>N/A</Text>
        ),
    },
    {
      Header: tableColumns.modefiedBy,
      disableSortBy: true,
      accessor: 'updated_by',
      Cell: (props: CellProps<DetectionAutoCloseConfig>) =>
        props.value !== 'N/A' ? (
          <Tooltip
            label={props.value}
            placement="top"
            isDisabled={props.value.length <= 20}
          >
            <Tag size="sm" colorScheme="purple" variant="solid">
              <AccountCircleSharpIcon color="text.primary" />
              <TagLabel ml={2} isTruncated={props.value.length > 20}>
                {props.value}
              </TagLabel>
            </Tag>
          </Tooltip>
        ) : (
          'N/A'
        ),
    },
    {
      Header: tableColumns.actions,
      Cell: (props: CellProps<DetectionAutoCloseConfig>) => (
        <ButtonGroup spacing={2}>
          <UpdateDetectionAutoCloseConfig
            detectionAutoCloseConfig={detectionAutoCloseConfigList}
            tenant={tenant ?? ''}
          />
          <ResetDetectionAutoCloseConfig
            detectionAutoCloseDuration={props.row.original.duration}
            tenant={tenant ?? ''}
          />
        </ButtonGroup>
      ),
    },
  ];

  return (
    <AccordionPanel
      title={title}
      reduceMotion={true}
      storageKey="detection-settings-panel"
      data-testid="detection-settings-panel"
    >
      <FlushPanelContent>
        <DataTable
          data={detectionAutoCloseConfigList}
          columns={dataTableColumns}
          isPaginable={false}
          isLined={true}
        />
      </FlushPanelContent>
    </AccordionPanel>
  );
};
